// ─── Shared brand tokens, helpers, and UI components ──────────────
// Used across all admin section files.

import { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useMarkers, addMarker, deleteMarker, findMarker, isMarkerRange, markerRangeId } from './timeframeMarkers';

// Brand tokens
export const C = {
  bg: '#FFF8F7', text: '#3D1F02', heading: '#6B3E0A', accent: '#AD795B',
  cta: '#F5A623', border: '#E8D5C4', sidebarBg: '#2A1400', sidebarActive: '#6B3E0A',
  card: '#fff', barTrack: '#F3E9DF',
  green: '#2E7D5E', greenBg: '#F0FAF5',
  amber: '#F5A623', amberBg: '#FFF8ED',
  red: '#C0392B', redBg: '#FEF2F0',
  blue: '#1D6FA4', blueBg: '#EDF5FB',
  chartBar: '#AD795B', chartGold: '#F5A623', chartLine: '#6B3E0A',
};

export const FONT = "'DM Sans', system-ui, sans-serif";

// ─── Helpers ──────────────────────────────────────────────────────
export const pct = (n, d) => (d ? ((n / d) * 100).toFixed(1) + '%' : '—');

// "+ N returning sign-ups" — the line beside an Opt-ins figure on Pages &
// Masterclass, Ad Metrics and Traffic & UTM. One wording for all three.
export const returningLine = (n) => `+ ${n} returning sign-up${n === 1 ? '' : 's'}`;

export const fmt$ = (n) =>
  n == null ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

// ── THE VIEWER'S OWN CALENDAR ────────────────────────────────────────────────
// Whole CALENDAR days between the day `when` falls on and today, both in the
// viewer's own time zone (this admin runs in their browser, so the browser's
// local date IS the viewer's). 0 = today, 1 = yesterday, -1 = tomorrow.
// Not a count of 24-hour blocks: 23:59 yesterday is 1, 00:01 today is 0.
// DISPLAY ONLY — no range, filter or count may use it.
export const calendarDaysAgo = (when, now = new Date()) => {
  const a = new Date(when), b = new Date(now);
  const da = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const db = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((db - da) / 86400000);
};

// "Today" / "Yesterday" / "3d ago" on the viewer's calendar. It used to count
// 24-hour blocks, so 11:44 yesterday viewed at 09:00 read "Today".
export const fmtRelative = (iso, now = new Date()) => {
  if (!iso) return '—';
  const days = Math.max(0, calendarDaysAgo(iso, now));
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7)  return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
};

export const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

// Formats a session datetime in the viewer's local timezone (auto-adjusts if Ben travels)
export const fmtSessionDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZoneName: 'short',
  });
};

// ─── Stage metadata ────────────────────────────────────────────────
export const STAGE_META = {
  lead:                    { label: 'Lead',                         color: C.accent,   bg: C.barTrack,  group: 'Leads'  },
  masterclass_watched:     { label: 'Masterclass Watched',          color: '#8B5E0A',  bg: '#F5E6CC',   group: 'Leads'  },
  purchased:               { label: 'RY Purchased',                 color: '#C47A1A',  bg: '#FDE8C0',   group: 'Portal' },
  course_active:           { label: 'Course Active',                color: C.heading,  bg: '#F3E9DF',   group: 'Portal' },
  course_complete:         { label: 'Course Complete',              color: C.green,    bg: C.greenBg,   group: 'Portal' },
  bms_booked:              { label: 'BMS Booked',                   color: C.green,    bg: C.greenBg,   group: 'Portal' },
  bms_noshow:              { label: 'BMS No-show',                  color: C.red,      bg: C.redBg,     group: 'Portal' },
  bms_attended_no_upsell:  { label: 'BMS Attended — No Upsell',     color: '#5A2D82',  bg: '#F3EDF9',   group: 'Portal' },
  // Manual-outcome stages (Stage 1) — only used when ry_bookings.bms_outcome is set.
  bms_show_upsell:         { label: 'BMS — Show + upsell',          color: '#FFF8F7',  bg: C.heading,   group: 'Portal' },
  bms_show_no_upsell:      { label: 'BMS — Show + no upsell',       color: C.blue,     bg: C.blueBg,    group: 'Portal' },
  bms_no_show:             { label: 'BMS — No-show',                color: C.red,      bg: C.redBg,     group: 'Portal' },
  uuu_client:              { label: 'UUU Client',                   color: '#FFF8F7',  bg: C.heading,   group: 'Portal' },
};

export const getStageFromRecord = (c) => STAGE_META[c.funnelStage] || STAGE_META.lead;

// ─── Timeframe control ──────────────────────────────────────────────
// ONE definition, shared by every tab that filters by date (Pages &
// Masterclass, Ad Metrics via paidOnly, Traffic & UTM). Do not fork this —
// a second copy is how two tabs' option sets silently drift apart.
const isoDay = (d) => d.toISOString().slice(0, 10);

// The backend clamps every lower bound to the launch date (14 August 2026 —
// floorFrom() in config/launch.js; everything earlier was pre-launch
// build/testing), so "All time" here honestly means "since launch" and a
// Custom range can't reach further back.
export const LAUNCH_DATE_UI = '2026-08-14';
// Sales attribution began two days after launch — earlier buyers carry no
// visitor id and can never be attributed to an ad. Any surface that says "from
// 16 Aug" should read it from here. (AdminCompare.jsx still carries its own
// SALES_FROM_ISO twin; consolidating that is a one-line change left for Ben.)
export const SALES_ATTRIBUTION_FROM_UI = '2026-08-16';

// 'last24h' is FIRST, before the previous shortest option (7d). Every other
// option is unchanged — same id, label and order as before.
//
// ── '14d' (Ben, 2026-09-05: "so I can quickly and easily also see 2 weeks
// worth of data") ──────────────────────────────────────────────────────────
// ONE LINE, ONE PLACE, THREE TABS. Pages & Masterclass, Ad Metrics (which is
// AdminPages with paidOnly) and Traffic & UTM all render TimeframeControl from
// this array, and AdminTraffic's period caption looks its label up here too
// (`RANGE_PRESETS.find(r => r.id === range)?.label`). Adding the option in six
// component files is exactly the drift this array exists to prevent.
//
// IT BEHAVES LIKE ITS NEIGHBOURS, NOT LIKE last24h. `days: 14` goes through
// the same branch as 7d and 30d in resolveTimeframe below: from = the calendar
// date 14x24h before now, to = today, both as bare YYYY-MM-DD, which the
// backend snaps to 00:00:00.000Z and 23:59:59.999Z. So it re-anchors to "now"
// every time the timeframe changes — it is not a fixed fortnight — but it is
// not a to-the-second rolling window either. last24h remains the only one of
// those, and deliberately: matching 7d and 30d is the point.
//
// NO BACKEND CHANGE. Nothing server-side knows about range ids; the frontend
// sends dates and config/launch.js parseRangeParam() takes any date it is
// given. floorFrom() clamps a lower bound earlier than 14 Aug 2026 up to the
// floor, identically for every preset — which is why 30d already reads the
// same as All time today, and why 14d will start doing so from 28 Aug 2026.
export const RANGE_PRESETS = [
  { id: 'last24h', label: 'Last 24 hours',          rolling: true },
  { id: '7d',      label: '7d',                     days: 7 },
  { id: '14d',     label: '14d',                    days: 14 },
  { id: '30d',     label: '30d',                    days: 30 },
  { id: '6m',      label: '6m',                     days: 182 },
  { id: 'all',     label: 'All time (since 14 Aug)', days: null },
  { id: 'custom',  label: 'Custom',                 days: null },
];

/**
 * Resolve the active preset (+ custom dates, if any) into {from, to}.
 *
 * Every preset but 'last24h' sends bare YYYY-MM-DD dates, exactly as before —
 * the backend day-snaps those to a day boundary. 'last24h' is the one
 * rolling window: a full ISO timestamp pair computed AT CALL TIME (now minus
 * 24h, to now), which the backend's widened date parser uses as given rather
 * than snapping to a day boundary — see parseRangeParam in
 * backend/src/config/launch.js. Recomputed on every call, so a refresh always
 * re-anchors to "now", not to whenever the tab first loaded.
 */
export function resolveTimeframe(range, customFrom, customTo) {
  const preset = RANGE_PRESETS.find(r => r.id === range);
  if (preset?.rolling) {
    const now = new Date();
    return {
      from: new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString(),
      to:   now.toISOString(),
    };
  }
  if (preset?.days) {
    const now = new Date();
    return {
      from: isoDay(new Date(now.getTime() - preset.days * 24 * 60 * 60 * 1000)),
      to:   isoDay(now),
    };
  }
  if (range === 'custom' && customFrom && customTo) {
    return { from: customFrom, to: customTo };
  }
  // ── A NAMED MARKER: "everything since that date" ──────────────────────
  // Same mechanism as the custom range — two bare YYYY-MM-DD dates the
  // backend day-snaps — with `to` being today, exactly as 7d/14d/30d send it.
  // This branch sits AFTER every built-in path so it cannot alter one.
  if (isMarkerRange(range)) {
    const m = findMarker(range);
    return m ? { from: m.date, to: isoDay(new Date()) } : { from: undefined, to: undefined };
  }
  return { from: undefined, to: undefined };
}

/** The label a tab prints for the selected range — preset, or "Since <marker>". */
export function rangeLabel(range) {
  const preset = RANGE_PRESETS.find(r => r.id === range);
  if (preset) return preset.label;
  const m = findMarker(range);
  return m ? `Since ${m.name}` : 'All time';
}

// ── THE DATES A RANGE ACTUALLY COVERS ────────────────────────────────────────
// A screenshot of "7d" is meaningless a month later, because "7d" means
// something different every day. These render the real span in plain text, so
// the picture carries its own dates.
//
// ⚠ THE LABEL MUST MATCH THE DATA, NOT THE BUTTON. Every funnel endpoint clamps
// its lower bound up to the launch date server-side (floorFrom in
// backend/src/config/launch.js), so "6m" does NOT show six months of figures —
// it shows 14 Aug onward. Printing "9 Mar – 7 Sep" over 14-Aug-onward data would
// be a confident lie, and worse than no label at all. So the clamp is applied
// here too, and said out loud when it bites.
const dmy = (iso) => new Date(`${String(iso).slice(0, 10)}T00:00:00Z`)
  .toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
const dm  = (iso) => new Date(`${String(iso).slice(0, 10)}T00:00:00Z`)
  .toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const dmyhm = (iso) => new Date(iso)
  .toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

/** "14 Aug – 7 Sep 2026", with the year written once when both ends share it. */
export function formatDaySpan(startISO, endISO) {
  const a = String(startISO).slice(0, 10), b = String(endISO).slice(0, 10);
  if (a === b) return dmy(a);
  return a.slice(0, 4) === b.slice(0, 4) ? `${dm(a)} – ${dmy(b)}` : `${dmy(a)} – ${dmy(b)}`;
}

/**
 * What the current selection really covers, after the launch-date clamp.
 * @returns {{text: string, note: string|null}}
 */
export function describeTimeframe(range, from, to) {
  // A bare date would be ambiguous for a rolling 24-hour window, so this one
  // carries the time as well. Shown in the reader's own timezone.
  if (range === 'last24h' && from && to) {
    return { text: `${dmyhm(from)} – ${dmyhm(to)}`, note: null };
  }
  const today = isoDay(new Date());
  const askedFrom = from ? String(from).slice(0, 10) : null;
  // No 'from' means all time, which BEGINS at the floor — that is its definition,
  // not a clamp, so it carries no warning.
  const clamped = !!askedFrom && askedFrom < LAUNCH_DATE_UI;
  const start = !askedFrom ? LAUNCH_DATE_UI : (clamped ? LAUNCH_DATE_UI : askedFrom);
  const end = to ? String(to).slice(0, 10) : today;
  return {
    text: formatDaySpan(start, end),
    note: clamped ? `starts at launch — nothing before ${dm(LAUNCH_DATE_UI)} is tracked` : null,
    // The marker's NAME, so the span line says which moment he is measuring
    // from — "Since Opt-in Copy Change · 24 Aug – 16 Sep 2026" — not just dates.
    title: isMarkerRange(range) ? (findMarker(range)?.name || null) : null,
  };
}

/**
 * The span line. Deliberately quiet — smaller and softer than the figures. It is
 * a label, not a headline. Plain text so a screenshot keeps it.
 */
export const RangeDates = ({ text, note, title, style = {} }) => (
  <div style={{ fontSize: 11, color: C.accent, fontFamily: FONT, lineHeight: 1.5,
    marginTop: -12, marginBottom: 16, ...style }}>
    {title ? <><strong style={{ color: C.text }}>Since {title}</strong> · </> : null}
    {text}{note ? <span style={{ opacity: 0.85 }}> · {note}</span> : null}
  </div>
);

/**
 * The button row + custom date inputs. Presentational and controlled — the
 * caller owns {range, customFrom, customTo} state (see useTimeframe below),
 * so each tab's timeframe stays independent of every other tab's, exactly as
 * it already was before this control was shared.
 *
 * flexWrap lets the row wrap onto a second line on a narrow phone rather than
 * widening the page — the admin shell already scrolls sideways on every tab,
 * and this control must not make that worse.
 */
const pillStyle = (on, extra = {}) => ({
  padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, fontFamily: FONT,
  border: `1px solid ${on ? C.heading : C.border}`,
  background: on ? C.heading : C.card,
  color: on ? '#FFF8F7' : C.accent, cursor: 'pointer', ...extra,
});

// ── MARKER CHIPS AND THE "+" FORM — SHARED BY THE TIMEFRAME CONTROL AND COMPARE
// One visual, two hosts. The delete "×" is hover-only where a pointer can hover
// (desktop) and always visible where it cannot (touch — Ben reads this on a
// phone). That is a CSS media query, injected once, because inline styles have
// no hover state. Built-in options never carry a delete affordance.
const MARKER_CSS = `
.ry-marker{position:relative;display:inline-flex;align-items:center}
.ry-marker-del{margin-left:-6px;width:20px;height:20px;border-radius:50%;border:1px solid ${C.border};background:${C.card};color:${C.accent};font-size:12px;line-height:18px;text-align:center;cursor:pointer;padding:0}
@media (hover: hover){.ry-marker-del{opacity:0;transition:opacity .12s}.ry-marker:hover .ry-marker-del,.ry-marker:focus-within .ry-marker-del{opacity:1}}
@media (hover: none){.ry-marker-del{width:28px;height:28px;line-height:26px;font-size:15px;margin-left:-8px}}
`;
let markerCssInjected = false;
const ensureMarkerCss = () => {
  if (markerCssInjected || typeof document === 'undefined') return;
  const el = document.createElement('style'); el.textContent = MARKER_CSS; document.head.appendChild(el);
  markerCssInjected = true;
};

/**
 * One saved marker as a selectable pill. Dashed border marks it as Ben's own,
 * not a built-in. `onDelete` is only passed for the owner; a viewer sees the
 * pill and nothing else.
 */
export const MarkerChip = ({ marker, on, disabled, title, onSelect, onDelete }) => {
  ensureMarkerCss();
  return (
    <span className="ry-marker">
      <button
        onClick={onSelect}
        disabled={disabled}
        title={title || `Since ${marker.date}`}
        style={pillStyle(on, {
          border: `1px ${on ? 'solid' : 'dashed'} ${on ? C.heading : C.accent}`,
          opacity: disabled ? 0.55 : 1, cursor: disabled ? 'not-allowed' : 'pointer',
          paddingRight: onDelete ? 18 : 14,
        })}
      >
        {marker.name}
      </button>
      {onDelete && (
        <button
          type="button"
          className="ry-marker-del"
          aria-label={`Delete marker ${marker.name}`}
          title="Delete this marker"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
        >×</button>
      )}
    </span>
  );
};

/**
 * The "+" control and its inline form. Owner-only by the caller's choice
 * (POST /settings is owner-only, so a viewer must never see it and 403).
 *
 * THE FLOOR IS ENFORCED IN THE PICKER, not after the fact: min is the launch
 * date, max is today. floorFrom() on the backend silently RAISES an earlier
 * date to the floor, so a marker dated 1 August would appear to work while
 * quietly showing data from 14 August — the picker makes that date unchoosable.
 * The same bounds are re-checked on save for a browser that lets a date be
 * typed past min/max.
 *
 * A failed save keeps every typed value on screen and says so in one line;
 * the marker is never shown as saved until the server has accepted it.
 */
export const AddMarkerControl = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const today = isoDay(new Date());

  const save = async () => {
    const n = name.trim();
    if (!n) return setError('Give the marker a name.');
    if (!date) return setError('Pick a date.');
    if (date < LAUNCH_DATE_UI) return setError(`Data starts ${dmy(LAUNCH_DATE_UI)} — pick that date or later.`);
    if (date > today) return setError('That date has not happened yet.');
    setSaving(true); setError(null);
    try {
      const m = await addMarker({ name: n, date });
      setName(''); setDate(''); setOpen(false);
      onCreated?.(m);
    } catch {
      setError('Marker not saved — check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} title="Add a named date marker"
        aria-label="Add a named date marker"
        style={pillStyle(false, { padding: '6px 11px', fontSize: 14, lineHeight: '14px' })}>+</button>
    );
  }
  return (
    <span style={{ display: 'flex', alignItems: 'flex-start', gap: 6, flexWrap: 'wrap', fontSize: 11, color: C.accent,
      border: `1px dashed ${C.accent}`, borderRadius: 12, padding: '8px 10px', background: C.card, maxWidth: '100%' }}>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <input type="date" value={date} min={LAUNCH_DATE_UI} max={today}
          onChange={e => { setDate(e.target.value); setError(null); }}
          style={{ fontFamily: FONT, fontSize: 11, padding: '5px 8px', border: `1px solid ${C.border}`, borderRadius: 6 }} />
        <span style={{ fontSize: 10, opacity: 0.85 }}>Data starts {dmy(LAUNCH_DATE_UI)}.</span>
      </span>
      <input type="text" value={name} placeholder="Name, e.g. Opt-in copy change" maxLength={60}
        onChange={e => { setName(e.target.value); setError(null); }}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setOpen(false); }}
        style={{ fontFamily: FONT, fontSize: 11, padding: '5px 8px', border: `1px solid ${C.border}`, borderRadius: 6, minWidth: 160 }} />
      <button onClick={save} disabled={saving} style={pillStyle(true, { opacity: saving ? 0.6 : 1 })}>
        {saving ? 'Saving…' : 'Save'}
      </button>
      <button onClick={() => { setOpen(false); setError(null); }} disabled={saving} style={pillStyle(false)}>Cancel</button>
      {error && (
        <span role="alert" style={{ flexBasis: '100%', fontSize: 11, fontWeight: 600, color: C.red }}>⚠ {error}</span>
      )}
    </span>
  );
};

/**
 * The button row + custom date inputs. Presentational and controlled — the
 * caller owns {range, customFrom, customTo} state (see useTimeframe below),
 * so each tab's timeframe stays independent of every other tab's, exactly as
 * it already was before this control was shared.
 *
 * flexWrap lets the row wrap onto a second line on a narrow phone rather than
 * widening the page — the admin shell already scrolls sideways on every tab,
 * and this control must not make that worse.
 *
 * MARKERS come after the built-ins, from the shared list in timeframeMarkers.js,
 * then the "+" (owner only). The built-in buttons are rendered exactly as before.
 *
 * `labels` (optional) renames a built-in button on ONE tab — { all: 'Every ON
 * period' } on Ads to Call — without changing what it sends. Absent → as before.
 */
export const TimeframeControl = ({ range, setRange, customFrom, setCustomFrom, customTo, setCustomTo, labels }) => {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const { markers, error: markersError } = useMarkers();

  const remove = async (m) => {
    if (!window.confirm(`Delete the marker "${m.name}"?`)) return;
    try {
      await deleteMarker(m.id);
      // Never leave the tab with nothing selected: fall back to the default.
      if (range === markerRangeId(m)) setRange('all');
    } catch {
      window.alert('Marker not deleted — check your connection and try again.');
    }
  };

  const today = isoDay(new Date());
  // What the From/To boxes show: Custom's own dates, or the selection's.
  const shown = range === 'custom' ? { from: customFrom, to: customTo } : shownDays(range);
  // Entering Custom starts from the dates on screen, so the boxes are never
  // blank and the figures do not jump to All time while a date is picked.
  const toCustom = (from, to) => { setCustomFrom(from); setCustomTo(to); setRange('custom'); };
  const editDate = (which, value) => {
    if (range === 'custom') return which === 'from' ? setCustomFrom(value) : setCustomTo(value);
    toCustom(which === 'from' ? value : shown.from, which === 'to' ? value : shown.to);
  };

  return (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 18 }}>
    {RANGE_PRESETS.map(r => (
      <button
        key={r.id}
        onClick={() => (r.id === 'custom' && range !== 'custom' ? toCustom(shown.from, shown.to) : setRange(r.id))}
        style={{
          padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, fontFamily: FONT,
          border: `1px solid ${range === r.id ? C.heading : C.border}`,
          background: range === r.id ? C.heading : C.card,
          color: range === r.id ? '#FFF8F7' : C.accent, cursor: 'pointer',
        }}
      >
        {labels?.[r.id] ?? r.label}
      </button>
    ))}
    {markers.map(m => (
      <MarkerChip key={m.id} marker={m} on={range === markerRangeId(m)}
        title={`Since ${dmy(m.date)}`}
        onSelect={() => setRange(markerRangeId(m))}
        onDelete={isOwner ? () => remove(m) : undefined} />
    ))}
    {isOwner && <AddMarkerControl onCreated={(m) => setRange(markerRangeId(m))} />}
    {markersError && <span style={{ fontSize: 10, color: C.accent, opacity: 0.8 }}>{markersError}</span>}
    {/* ── FROM / TO, ALWAYS ON SCREEN, ON THEIR OWN LINE ──────────────────
        These used to appear only under Custom, unlabelled and EMPTY, tucked
        after the markers and "+". An empty date box can render as next to
        nothing on a phone, so they read as gone. Now they always show the
        dates the figures cover; editing either one switches to Custom from
        those dates. Custom works exactly as before: two bare YYYY-MM-DD dates,
        day-snapped by the backend like every preset. */}
    <span style={{ flexBasis: '100%', display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontFamily: FONT, color: C.accent, flexWrap: 'wrap', marginTop: 4 }}>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        From
        <input type="date" aria-label="From date" value={shown.from}
          min={LAUNCH_DATE_UI} max={shown.to || today}
          onChange={e => editDate('from', e.target.value)} style={dateStyle} />
      </label>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        To
        <input type="date" aria-label="To date" value={shown.to}
          min={shown.from || LAUNCH_DATE_UI} max={today}
          onChange={e => editDate('to', e.target.value)} style={dateStyle} />
      </label>
      {range === 'custom' && customFrom && customFrom < LAUNCH_DATE_UI && (
        <span style={{ fontSize: 10, color: '#92650A', background: C.amberBg, border: '1px solid #F5D69A', borderRadius: 6, padding: '3px 8px' }}>
          Data starts {dmy(LAUNCH_DATE_UI)} (launch) — earlier dates are clamped
        </span>
      )}
    </span>
  </div>
  );
};

// The From/To boxes. A min-width so an empty box can never collapse to
// nothing (the reason they looked gone on a phone).
const dateStyle = { fontFamily: FONT, fontSize: 11, padding: '5px 8px', border: `1px solid ${C.border}`, borderRadius: 6, minWidth: 128, background: C.card, color: C.text };

/**
 * The days a NON-custom selection covers, as the From/To boxes show them —
 * the same days resolveTimeframe sends, with the launch-date clamp applied the
 * way describeTimeframe applies it to the span line. Display only.
 */
function shownDays(range) {
  if (range === null || range === undefined) return { from: '', to: '' };
  const { from, to } = resolveTimeframe(range, '', '');
  const f = from ? String(from).slice(0, 10) : LAUNCH_DATE_UI;
  return {
    from: f < LAUNCH_DATE_UI ? LAUNCH_DATE_UI : f,
    to: to ? String(to).slice(0, 10) : isoDay(new Date()),
  };
}

/**
 * The control with its date span underneath. Tabs render THIS rather than
 * TimeframeControl, so the dates cannot be forgotten on a new tab.
 */
export const TimeframeControlWithDates = (props) => {
  const { from, to } = resolveTimeframe(props.range, props.customFrom, props.customTo);
  const span = describeTimeframe(props.range, from, to);
  return (
    <>
      <TimeframeControl {...props} />
      <RangeDates text={span.text} note={span.note} title={span.title} />
    </>
  );
};

/**
 * Bundles the control's state + resolved {from, to} for one tab. Each tab
 * that renders TimeframeControl calls this once, in its own component — state
 * lives there (not lifted, not global), so tab-switch reset behaviour is
 * unchanged: every tab still gets a fresh 'all' default on remount.
 *
 * from/to are memoised on [range, customFrom, customTo] — NOT recomputed on
 * every render. That matters for 'last24h': it resolves to `new Date()`, so
 * without memoisation every unrelated re-render (a fetch completing, a
 * loading flag flipping) would produce a millisecond-different `to`, which
 * would retrigger any effect keyed on it — an infinite fetch loop. Recomputed
 * exactly when the user changes the timeframe (or the tab remounts), which is
 * what "at request time" means here — not "every React render".
 */
export function useTimeframe(defaultRange = 'all', { preferMarkerName = null } = {}) {
  // ── OPENING ON A SAVED MARKER (Ben, 2026-09-30) ─────────────────────────
  // With preferMarkerName, the tab opens on that saved marker ("Opt-in Copy
  // Change") — following the marker's STORED date, never a date written here.
  // Markers load from the server, so until the list has arrived (or failed, or
  // 4 seconds have passed) the range is not decided: `ready` is false, range
  // is null and from/to are undefined, and the tab holds its fetches rather
  // than loading "All time" and then loading again. No such marker, a failed
  // load, or the timeout → the tab's own default, exactly as before.
  const { markers, loaded } = useMarkers();
  const pick = () => {
    const m = findMarkerByName(markers, preferMarkerName);
    return m ? markerRangeId(m) : defaultRange;
  };
  const [range, setRange] = useState(() => (!preferMarkerName ? defaultRange : (loaded ? pick() : null)));
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    if (range !== null) return undefined;
    if (loaded) { setRange(pick()); return undefined; }
    const t = setTimeout(() => setTimedOut(true), 4000);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, range]);
  useEffect(() => { if (timedOut && range === null) setRange(defaultRange); }, [timedOut, range, defaultRange]);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const { from, to } = useMemo(
    () => (range === null ? { from: undefined, to: undefined } : resolveTimeframe(range, customFrom, customTo)),
    [range, customFrom, customTo]
  );
  return { range, setRange, customFrom, setCustomFrom, customTo, setCustomTo, from, to, ready: range !== null };
}

/** The saved marker to open on by default (matched by name, case-insensitive). */
export const DEFAULT_MARKER_NAME = 'Opt-in Copy Change';

/** A saved marker by name; if several share it, the most recent date. */
export function findMarkerByName(markers, name) {
  if (!name) return null;
  const want = String(name).trim().toLowerCase();
  const hits = (markers || []).filter(m => String(m.name || '').trim().toLowerCase() === want);
  if (!hits.length) return null;
  return hits.reduce((a, b) => (b.date > a.date ? b : a));
}

// ─── Shared UI components ─────────────────────────────────────────
export const Card = ({ title, children, style = {} }) => (
  <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '20px 24px', ...style }}>
    {title && (
      <div style={{ fontSize: 12, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>
        {title}
      </div>
    )}
    {children}
  </div>
);

export const StatTile = ({ label, value, sub, accent }) => (
  <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '20px 22px', flex: 1, minWidth: 140 }}>
    <div style={{ fontSize: 11, fontWeight: 600, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>{label}</div>
    <div style={{ fontSize: 32, fontWeight: 800, color: accent || C.heading, lineHeight: 1 }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: C.accent, marginTop: 6 }}>{sub}</div>}
  </div>
);

export const StagePill = ({ stage }) => (
  <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, color: stage.color, background: stage.bg, whiteSpace: 'nowrap' }}>
    {stage.label}
  </span>
);

export const SectionHeader = ({ title, sub }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontSize: 15, fontWeight: 800, color: C.heading }}>{title}</div>
    {sub && <div style={{ fontSize: 11, color: C.accent, marginTop: 2 }}>{sub}</div>}
  </div>
);

// ─── Charts ───────────────────────────────────────────────────────
export const MiniBarChart = ({ data, valueKey, labelKey, color = C.chartBar, height = 80 }) => {
  const max = Math.max(...data.map(d => d[valueKey]));
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <div style={{ fontSize: 9, color: C.accent, fontWeight: 600 }}>{d[valueKey]}</div>
          <div style={{ width: '100%', background: color, height: Math.max(4, (d[valueKey] / max) * (height - 24)), borderRadius: '3px 3px 0 0', opacity: 0.85 }} />
          <div style={{ fontSize: 9, color: C.accent, textAlign: 'center' }}>{d[labelKey]}</div>
        </div>
      ))}
    </div>
  );
};

export const LineChart = ({ data }) => {
  const W = 100, H = 70;
  const max = Math.max(...data.map(d => d.rev));
  const pts = data.map((d, i) => { const x = (i / (data.length - 1)) * W; const y = H - (d.rev / max) * (H - 8) - 4; return `${x},${y}`; });
  const area = `0,${H} ${pts.join(' ')} ${W},${H}`;
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 80, overflow: 'visible' }}>
        <defs>
          <linearGradient id="lg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={C.chartLine} stopOpacity="0.25" />
            <stop offset="100%" stopColor={C.chartLine} stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points={area} fill="url(#lg)" />
        <polyline points={pts.join(' ')} fill="none" stroke={C.chartLine} strokeWidth="1.5" />
        {data.map((d, i) => { const [x, y] = pts[i].split(',').map(Number); return <circle key={i} cx={x} cy={y} r="2.5" fill={C.cta} />; })}
      </svg>
      <div style={{ display: 'flex' }}>
        {data.map((d, i) => <span key={i} style={{ fontSize: 9, color: C.accent, textAlign: 'center', flex: 1 }}>{d.month}</span>)}
      </div>
    </div>
  );
};

export const GroupedBarChart = ({ data, height = 100 }) => {
  const max = Math.max(...data.map(d => Math.max(d.ry, d.uuu)));
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height }}>
        {data.map((d, i) => (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <div style={{ width: '100%', display: 'flex', gap: 2, alignItems: 'flex-end' }}>
              <div style={{ flex: 1, background: C.chartBar, height: Math.max(3, (d.ry / max) * (height - 20)), borderRadius: '2px 2px 0 0' }} />
              <div style={{ flex: 1, background: C.chartGold, height: Math.max(3, (d.uuu / max) * (height - 20)), borderRadius: '2px 2px 0 0' }} />
            </div>
            <div style={{ fontSize: 8, color: C.accent, marginTop: 4, textAlign: 'center' }}>{d.month}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 14, marginTop: 8 }}>
        {[['RY', C.chartBar], ['UUU', C.chartGold]].map(([l, col]) => (
          <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: C.accent }}>
            <div style={{ width: 10, height: 10, background: col, borderRadius: 2 }} /> {l}
          </div>
        ))}
      </div>
    </div>
  );
};
